import { useEffect, useRef, useState } from 'react';

const slides = [
    {
        name: 'Legal Assistant',
        brandPrefix: 'Podus AI',
        logo: '/logo/podus-ai.svg',
        image: '/services/legal_assistant.svg',
        description:
            'Podus is an AI-powered super app transforming how people and businesses access legal solutions — from instant guidance to contract management and human rights protection.',
    },
    {
        name: 'Checker',
        brandPrefix: 'Podus',
        logo: '/logo/podus-cheaker.svg',
        image: '/services/checker.svg',
        description:
            'Run instant background checks on individuals and businesses with verified data — making informed decisions faster and with greater confidence.',
    },
    {
        name: 'Contracts',
        brandPrefix: 'Podus',
        logo: '/logo/podus-contract.svg',
        image: '/services/contract.svg',
        description:
            'Draft, review, and manage contracts effortlessly with AI-powered templates and smart legal guidance designed for individuals and businesses.',
    },
    {
        name: 'Appointment',
        brandPrefix: 'Podus',
        logo: '/logo/podus-appointment.svg',
        image: '/services/appointment.svg',
        description:
            'Book verified legal professionals in minutes. Get the right expert for your case without the hassle of long searches or uncertain credentials.',
    },
    {
        name: 'Aid',
        brandPrefix: 'Podus',
        logo: '/logo/podus-aids.svg',
        image: '/services/aids.svg',
        description:
            'Access legal aid and human rights support when you need it most. Podus Aid connects you with resources and professionals who can help.',
    },
];

type Phase = 'idle' | 'exit' | 'enter';

export function ContractIllustration() {
    const [active, setActive] = useState(0);
    const [phase, setPhase] = useState<Phase>('idle');
    const pendingRef = useRef<number | null>(null);

    const transition = (next: number) => {
        if (next === active || phase !== 'idle') return;
        setPhase('exit');
        setTimeout(() => {
            setActive(next);
            setPhase('enter');
            setTimeout(() => setPhase('idle'), 420);
        }, 260);
    };

    useEffect(() => {
        const timer = setInterval(() => {
            pendingRef.current = (active + 1) % slides.length;
            transition(pendingRef.current);
        }, 3500);
        return () => clearInterval(timer);
    }, [active, phase]);

    const slide = slides[active];
    const animClass = phase === 'exit' ? 'carousel-exit' : phase === 'enter' ? 'carousel-enter' : '';

    return (
        <div className="flex flex-col gap-7 overflow-hidden">
            {/* Image */}
            <div className={`w-full ${animClass}`}>
                <img
                    src={slide.image}
                    alt={`${slide.brandPrefix} ${slide.name}`}
                    className="h-auto w-full max-w-131.75 rounded-2xl object-cover"
                />
            </div>

            {/* Title + description */}
            <div className={`flex flex-col gap-1.75 ${animClass}`}>
                <div className="flex items-center gap-2">
                    <img
                        src={slide.logo}
                        alt={`${slide.brandPrefix} ${slide.name} logo`}
                        className="h-6 w-6 rounded-md object-contain"
                    />
                    <h3 className="text-base font-bold tracking-tight text-foreground">
                        {slide.brandPrefix} {slide.name}
                    </h3>
                </div>
                <p className="text-sm leading-[1.7] text-muted-foreground">{slide.description}</p>
            </div>

            {/* Carousel dots */}
            <div className="flex items-center gap-2">
                {slides.map((_, i) => (
                    <button
                        key={i}
                        type="button"
                        aria-label={`Go to slide ${i + 1}`}
                        onClick={() => transition(i)}
                        className={`cursor-pointer rounded-full transition-all duration-300 ${
                            active === i ? 'h-1.75 w-4.75 bg-btn' : 'h-1.75 w-1.75 bg-text-muted'
                        }`}
                    />
                ))}
            </div>
        </div>
    );
}
